import React from "react";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
import { ROLES } from "../config/roles";
import {
	Chat,
	Channel,
	ChannelList,
	Window,
	MessageList,
	LoadingIndicator,
	useChatContext,
	Streami18n,
} from "stream-chat-react";
import "stream-chat-react/dist/css/v2/index.css";
import {
	StreamCall,
	StreamVideo,
	StreamTheme,
	BackgroundFiltersProvider,
} from "@stream-io/video-react-sdk";
import "@stream-io/video-react-sdk/dist/css/styles.css";
import CustomAttachment from "../components/chat/CustomAttachment";
import CustomChannelPreview from "../components/inbox/CustomChannelPreview";
import InboxTabs from "../components/inbox/InboxTabs";
import InboxArchiveSection, {
	ArchiveGroupHeader,
} from "../components/inbox/InboxArchiveSection";
import MessageRequestsPanel from "../components/inbox/MessageRequestsPanel";
import RequestDetailPane from "../components/inbox/RequestDetailPane";
import NewChatModal from "../components/inbox/NewChatModal";
import InboxSearchResults from "../components/inbox/InboxSearchResults";
import DmIntentChips from "../components/inbox/DmIntentChips";
import LawRequestsSection from "../components/inbox/LawRequestsSection";
import CustomChatHeader from "../components/inbox/CustomChatHeader";
import CustomWindow from "../components/inbox/CustomWindow";
import ParticipantsRail from "../components/inbox/ParticipantsRail";
import CustomMessage from "../components/inbox/CustomMessage";
import {
	SelectAChatPlaceholder,
	NoMessagesYetIndicator,
} from "../components/inbox/threadEmptyStates";
import CustomMessageInput from "../components/inbox/CustomMessageInput";
import ChatDateSeparator from "../components/inbox/ChatDateSeparator";
import CallModal from "../components/inbox/call/CallModal";
import IncomingCallScreen from "../components/inbox/call/IncomingCallScreen";
import useInbox from "../hooks/inbox/useInbox";
import useTranslate from "../hooks/useTranslate";
import { useMediaQuery } from "../hooks/useMediaQuery";
import useSupportFabLift from "../hooks/useSupportFabLift";
import {
	toStreamChatLanguage,
	STREAM_CHAT_FALLBACK_LANGUAGE,
} from "../utils/streamChatLanguage";

// D29: which message-request origin a lane hosts (DM · Last Minute · Agent Req.
// only). Club Req. / Trusted Agent / Scout Req. never host agent↔agent requests.
const LANE_REQUEST_ORIGIN = {
  dm: "direct",
  last_minute: "last_minute",
  agent_requests: "agent_requests",
};

// Mobile keeps FULL tab labels (D17 / CANON-SPECS 5k) — desktop abbreviates.
const MOBILE_TAB_LABELS = {
  agent_requests: "Agent Requests",
  club_requests: "Club Requests",
  scout_requests: "Scout Requests",
  trusted_agent: "Trusted Agent",
  last_minute: "Last Minute",
  dm: "DM",
};

const streamOverrides = `
	/* Chat viewport frame. iOS Safari's 100vh includes the area BEHIND the
	   collapsing browser chrome, so a 100vh-sized chat column pushed the
	   composer below the visible viewport (Marin, staging iOS — the input
	   row + icons were cut off even with the toolbar hidden). dvh tracks
	   the visible viewport; the vh line stays first as the fallback for
	   browsers without dvh. The safe-area inset keeps the composer clear
	   of the iOS home indicator when Safari's chrome is minimised
	   (env() resolves to 0 everywhere else). 88px = app header. */
	.tp-inbox-viewport {
		height: calc(100vh - 88px);
		height: calc(100dvh - 88px);
		padding-bottom: env(safe-area-inset-bottom, 0px);
	}

	/* Reset and base styles */
	.str-chat__channel-list-messenger {
		padding: 0;
		background: transparent;
	}
	.str-chat__virtual-list {
		border-top: none !important;
		background: transparent;
	}
	.str-chat__virtual-list__item {
		border-bottom: none !important;
		background: transparent;
		padding: 0;
		margin: 0 0 4px 0;
	}
	.str-chat__channel-preview-messenger {
		border: none;
		background: none;
		padding: 0;
		margin: 0;
		width: 100%;
		border-radius: 0;
		box-shadow: none;
	}
	.str-chat__channel-preview-messenger:hover {
		background: none;
	}
	.str-chat__list {
		padding-bottom: 0 !important;
	}

	/* Channel layout. flex sizing, NOT height:100% — the channel sits in a
	   flex column BELOW the 44px mobile back bar ("< Messages"), and
	   height:100% measured the whole column, ignoring that sibling: the
	   channel overflowed the frame by exactly the bar's height and the
	   composer's lower half was clipped on phones (Marin, staging iOS).
	   Desktop is unchanged — the bar is hidden there, so flex:1 resolves
	   to the same full height. */
	.str-chat__channel {
		flex: 1;
		min-height: 0;
		display: flex;
		flex-direction: column;
		background: white;
	}

	/* Main panel */
	.str-chat__main-panel {
		flex: 1;
		min-height: 0;
		display: flex;
		flex-direction: column;
		background: white;
	}

	/* Message list */
	.str-chat__message-list-scroll {
		flex-grow: 1;
		min-height: 0;
		background: white;
		padding: 24px 20px;
	}

	.str-chat__message-list {
		padding: 0;
	}

	.str-chat__message {
		margin: 0;
		padding: 0;
	}

	.str-chat__message-wrapper {
		padding: 0;
		margin: 0;
	}

	/* Message input */
	.str-chat__input-flat {
		border: none;
		background: transparent;
		padding: 0;
		flex-shrink: 0;
	}

	/* Message styling - Override default Stream styles */
	.str-chat__message--me .str-chat__message-bubble,
	.str-chat__message:not(.str-chat__message--me) .str-chat__message-bubble {
		display: none; /* Hide default message bubbles since we're using custom */
	}

	.str-chat__message-team,
	.str-chat__message-simple {
		display: flex;
		flex-direction: column;
		width: 100%;
	}

	.str-chat__message-team__avatar,
	.str-chat__message-simple__avatar {
		display: none; /* Hide default avatars since we're using custom */
	}

	.str-chat__message-team__content,
	.str-chat__message-simple__content {
		width: 100%;
		margin: 0;
		padding: 0;
	}

	.str-chat__message-data {
		margin: 0;
		padding: 0;
	}

	/* Hide default header */
	.str-chat__header-livestream {
		display: none;
	}

	/* Video Call Enhancements */
	.str-video {
		background-color: #1a1a1a !important;
		color: #ffffff !important;
		height: 100% !important;
		width: 100% !important;
		display: flex !important;
		flex-direction: column !important;
		min-width: 0 !important;
		max-width: 100% !important;
	}

	/* Participant video styling */
	.str-video__participant-view {
		border-radius: 12px !important;
		overflow: hidden !important;
	}

	/* Speaker layout enhancements */
	.str-video__speaker-layout {
		background: linear-gradient(135deg, #1a1a1a 0%, #2d2d2d 100%) !important;
	}

	/* Call controls styling */
	.str-video__call-controls {
		background: rgba(0, 0, 0, 0.8) !important;
		backdrop-filter: blur(10px) !important;
		border-top: 1px solid rgba(255, 255, 255, 0.1) !important;
	}

	/* Participant bar styling */
	.str-video__participants-bar {
		background: rgba(0, 0, 0, 0.6) !important;
		backdrop-filter: blur(10px) !important;
		border-top: 1px solid rgba(255, 255, 255, 0.1) !important;
	}

	/* Network quality indicator */
	.str-video__network-quality {
		background: rgba(0, 0, 0, 0.7) !important;
		border-radius: 4px !important;
	}

	/* Speaking indicator */
	.str-video__speaking-indicator {
		border: 3px solid #00ff88 !important;
		box-shadow: 0 0 20px rgba(0, 255, 136, 0.5) !important;
	}

	/* Screen share indicator */
	.str-video__screen-share-indicator {
		background: rgba(59, 130, 246, 0.9) !important;
		color: white !important;
		border-radius: 6px !important;
		padding: 4px 8px !important;
		font-size: 12px !important;
		font-weight: 600 !important;
	}

	/* Recording indicator */
	.str-video__recording-indicator {
		background: rgba(239, 68, 68, 0.9) !important;
		color: white !important;
		border-radius: 6px !important;
		padding: 4px 8px !important;
		font-size: 12px !important;
		font-weight: 600 !important;
		animation: pulse 2s infinite !important;
	}

	@keyframes pulse {
		0%, 100% { opacity: 1; }
		50% { opacity: 0.5; }
	}

	/* Floating local video */
	.str-video__floating-local {
		box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3) !important;
		border: 2px solid rgba(255, 255, 255, 0.2) !important;
		transition: all 0.3s ease !important;
	}

	.str-video__floating-local:hover {
		transform: scale(1.05) !important;
		border-color: rgba(255, 255, 255, 0.4) !important;
	}

	/* Call status animations */
	.str-video__status-indicator {
		animation: fadeInOut 2s ease-in-out infinite !important;
	}

	@keyframes fadeInOut {
		0%, 100% { opacity: 0.7; }
		50% { opacity: 1; }
	}
`;

// QA-3 / 5k+5n bridge: a normal row click sets STREAM's context channel, never
// useInbox's activeChannel state (the A2/rail lesson) — so the mobile
// single-column collapse must observe the ChatContext. Rendered INSIDE <Chat>;
// mirrors the live channel upward and exposes the context setter for the
// mobile back control. Renders nothing.
const ChatActiveChannelBridge = ({ onChannel, setterRef }) => {
	const { channel, setActiveChannel } = useChatContext();
	React.useEffect(() => {
		setterRef.current = setActiveChannel;
		onChannel(channel || null);
	}, [channel, setActiveChannel, onChannel, setterRef]);
	return null;
};

const Inbox = () => {
	const {
		chatClient,
		videoClient,
		userId,
		activeChannel,
		setActiveChannel,
		sidebarSearchQuery,
		setSidebarSearchQuery,
		filters,
		archivedFilters,
		sort,
		options,
		activeTab,
		setActiveTab,
		tabCounts,
		tabTotals,
		archivedCounts,
		searchScope,
		setSearchScope,
		setPendingRequestsCount,
		setPendingRequestsByLane,
		listChannelFilterFn,
		archiveManualFilterFn,
		archiveExpiredFilterFn,
		archiveChat,
		unarchiveChat,
		listRefreshKey,
		intentFilter,
		setIntentFilter,
		dmIntentCounts,
		inboxModel,
		openThreadByCid,
		handleSidebarSearch,
		handleSidebarSearchKeyDown,
		isLoading,
		handleStartCall,
		handleEndCall,
		goToMeetings,
		incomingCall,
		showIncomingCallModal,
		acceptIncomingCall,
		declineIncomingCall,
		isInCall,
		call,
		callType,
	} = useInbox();

	const { t, currentLanguage } = useTranslate();
	const [showArchive, setShowArchive] = React.useState(false);

	// DECISION_026 — the chat chrome follows OUR language selector.
	// Without an `i18nInstance` the library picks the language itself
	// (client.user.language → browser locale → 'en'), a chain our selector is not
	// part of; that is how an EN seat got German date chrome ("heute um …").
	// Handing <Chat> an explicit instance skips that chain entirely.
	const streamChatLanguage = toStreamChatLanguage(currentLanguage);
	const streami18nRef = React.useRef(null);
	if (!streami18nRef.current) {
		streami18nRef.current = new Streami18n({ language: streamChatLanguage });
	}
	const streami18n = streami18nRef.current;
	// Language switches must land without a reload. The instance identity stays
	// stable on purpose (useChat only re-reads it when it changes); setLanguage
	// pushes the new `t` through the library's own callback and the date parser
	// reads `currentLanguage` on every call, so the chrome re-renders in place.
	React.useEffect(() => {
		streami18n.setLanguage(streamChatLanguage);
	}, [streami18n, streamChatLanguage]);

	// DESIGN_006: frame 1r's escape hatch "View your N pending requests" deep-links
	// to /inbox?tab=dm&requests=sent. Read once at mount — useInbox strips the query
	// string after it has consumed ?tab= / ?channel=, so a later read would miss it.
	const [requestsView, setRequestsView] = React.useState(() => {
		try {
			return new URLSearchParams(window.location.search).get("requests") === "sent"
				? "sent"
				: "received";
		} catch (e) {
			return "received";
		}
	});

	// #11: two-pane "Requests" gate (design 1f). The selected request drives the
	// right-hand detail pane; the panel remains the single owner of the list +
	// accept/ignore flows, which it hands up through onProvideActions.
	const [selectedRequest, setSelectedRequest] = React.useState(null);
	const requestActionsRef = React.useRef({ accept: null, ignore: null });

	// #12: "New chat" picker (design 1a "+").
	const [showNewChat, setShowNewChat] = React.useState(false);

	// Tab bar model (D1/D20): perspective-specific tab set from the role model,
	// translated labels, per-lane counts, and the mobile full-label variant.
	const inboxTabs = (inboxModel?.tabs || []).map((tab) => ({
		key: tab.key,
		label: t(tab.label),
		mobileLabel: t(MOBILE_TAB_LABELS[tab.key] || tab.label),
		count: tabCounts?.[tab.key] || 0,
	}));
	const showTabBar = Boolean(inboxModel?.showTabBar);

	// D29: the message-request origin the active lane hosts (or null → the lane
	// has no cold-contact gate: Club Req. / Trusted Agent / Scout Req.). The gate
	// is AGENT-only — message requests are agent↔agent; a club never RECEIVES one
	// (clubs contact agents directly), so a club-only viewer mounts no gate on any
	// lane (incl. its own DM / Last Minute tabs). Otherwise the club DM tab fired
	// GET received?origin=direct → 403 for ClubAdmin → a red error banner (QA S2).
	const activeLaneRequestOrigin = inboxModel?.agentCapable
		? LANE_REQUEST_ORIGIN[activeTab] || null
		: null;

	// QA-3 / 5k+5n: below md the inbox is SINGLE-COLUMN. With a thread or a
	// request detail open, the list collapses and the thread pane fills the width
	// (with a mobile back control); otherwise the list is full-width and the
	// thread pane is hidden. Desktop (md+) keeps the two-pane layout unchanged.
	// Context-channel mirror (set by the bridge inside <Chat>) — covers the
	// click-open path; useInbox's activeChannel covers deep-links before the
	// context syncs.
	const [ctxChannel, setCtxChannel] = React.useState(null);
	const ctxSetterRef = React.useRef(null);
	// Clears the open thread in BOTH sources (Stream's context channel and
	// useInbox's activeChannel). Used by the mobile back control and by
	// onChannelLeave — clearing only one of them would leave the other still
	// feeding the pane.
	const clearOpenThread = React.useCallback(() => {
		ctxSetterRef.current?.(undefined);
		setActiveChannel(null);
	}, [setActiveChannel]);
	const hasThreadOpen =
		Boolean(activeChannel) || Boolean(ctxChannel) || Boolean(selectedRequest);

	// Mobile thread view: the composer is a fixed bottom row, and the app-wide
	// support button's resting corner (24px inset) sat exactly on its send
	// button (UI rule 2: nothing may be covered by the floating button). Lift
	// it above the composer while a thread is open, via the same declaration
	// mechanism the My Agency invite bar uses. Desktop keeps the resting spot —
	// the composer column ends well left of the button there.
	const isMobileChatViewport = useMediaQuery("(max-width: 767px)");
	useSupportFabLift(hasThreadOpen && isMobileChatViewport ? 76 : 0);

	// T031 (symptom 1): a deep-linked thread — profile "Message" → /inbox with
	// state.directChat, ?channel=…, or the Requests panel "Open →" — arrives on
	// useInbox's activeChannel. Hand it straight over to STREAM's context
	// channel: the list-row highlight and every later row click key off the
	// context, and <Channel channel={…}> lets a lingering prop channel win over
	// the context (stream-chat-react: `propsChannel || contextChannel`), which
	// would freeze the pane on the deep-linked thread. Once handed over, the
	// hook slot is cleared so the context is the single source of truth.
	React.useEffect(() => {
		if (activeChannel && ctxSetterRef.current) {
			ctxSetterRef.current(activeChannel);
			setActiveChannel(null);
		}
	}, [activeChannel, setActiveChannel]);

	// 4b: scope chips surface only while the inbox search has focus (or a query is
	// present); the resting list stays exactly as it is.
	const [searchFocused, setSearchFocused] = React.useState(false);

	// 4b scope chips — counterpart type. "Search in:" + All/Clubs/Agents/Staff/
	// Players, matching the design's chip taxonomy. Clubs/Agents bite on real
	// data; Staff/Players are honest scopes that rarely match.
	const searchScopes = [
		{ key: "all", label: "All" },
		{ key: "clubs", label: "Clubs" },
		{ key: "agents", label: "Agents" },
		{ key: "staff", label: "Staff" },
		{ key: "players", label: "Players" },
	];
	const showScopeChips = searchFocused || Boolean(sidebarSearchQuery);

	// D24: per-tab archive counts, split by species (frame 5i). archivedCounts is
	// { [lane]: {manual, expired} }; the footer reads "N archived by you · M
	// expired" and the two groups render distinguishably.
	const archiveBuckets = archivedCounts?.[activeTab] || { manual: 0, expired: 0 };
	const archiveManualCount = archiveBuckets.manual || 0;
	const archiveExpiredCount = archiveBuckets.expired || 0;

	// D24 row actions: archive from the main list, unarchive from the archive.
	// The hook owns the API call + list refresh; here we only surface the 409
	// (expired = read-only, revive-only) pointer.
	const handleArchiveRow = React.useCallback(
		async (channel) => {
			const res = await archiveChat(channel);
			if (!res?.success) toast.error(res?.error || "Couldn't archive this chat.");
		},
		[archiveChat],
	);
	const handleUnarchiveRow = React.useCallback(
		async (channel) => {
			const res = await unarchiveChat(channel);
			if (!res?.success) {
				toast.error(
					res?.expired
						? res?.error ||
							"This chat expired and is read-only — the club can revive it."
						: res?.error || "Couldn't unarchive this chat.",
				);
			}
		},
		[unarchiveChat],
	);

	// Stable Preview wrappers per list context (main / manual archive / expired
	// archive) so the ChannelList rows keep the right row action without
	// remounting on every render.
	const MainPreview = React.useCallback(
		(p) => <CustomChannelPreview {...p} variant="main" onArchive={handleArchiveRow} />,
		[handleArchiveRow],
	);
	const ArchiveManualPreview = React.useCallback(
		(p) => (
			<CustomChannelPreview
				{...p}
				variant="archive-manual"
				onUnarchive={handleUnarchiveRow}
			/>
		),
		[handleUnarchiveRow],
	);
	const ArchiveExpiredPreview = React.useCallback(
		(p) => <CustomChannelPreview {...p} variant="archive-expired" />,
		[],
	);

	// Empty-tab copy (design 1b). The Agent Req. tab carries verbatim design copy;
	// other tabs use a faithful generalization. (Stream renders this only when the
	// underlying query is empty; a tab that filters to zero while other tabs hold
	// channels is a known ChannelList render-filter limit.)
	const listEmptyCopy = {
		agent_requests: {
			title: "No Agent Request chats yet",
			body: "When you connect with another agent around an Agent Request, each accepted contact becomes its own conversation here.",
		},
	};
	const ListEmptyState = () => {
		const label =
			inboxTabs.find((tab) => tab.key === activeTab)?.label || "";
		const copy = listEmptyCopy[activeTab] || {
			title: `No ${label} chats yet`,
			body: "Accepted conversations for this tab appear here — each contact becomes its own thread.",
		};
		return (
			<div className="flex flex-col items-center justify-center text-center px-6 py-12 gap-1.5">
				<p className="text-[13.5px] font-semibold text-[#344054]">
					{copy.title}
				</p>
				<p className="text-[11.5px] text-[#98A2B3] leading-relaxed max-w-[240px]">
					{copy.body}
				</p>
			</div>
		);
	};

	// D29: a selected cold-contact request opens the detail pane on the right. It
	// is scoped to the active lane; switching lanes clears any stale selection.
	React.useEffect(() => {
		setSelectedRequest(null);
	}, [activeTab]);

	// #23: reliable per-tab empty state. Stream's ChannelList EmptyStateIndicator
	// only fires when the SERVER query is empty — never when the active tab's
	// client render-filter narrows to zero while other tabs still hold channels.
	// When some tab has channels (total > 0) but the active tab has none, render
	// our own empty state; the all-empty case still falls to the ChannelList's own
	// indicator, so the two never double up.
	const totalAcrossTabs = Object.values(tabTotals || {}).reduce(
		(sum, n) => sum + (n || 0),
		0,
	);
	const showTabEmptyState =
		totalAcrossTabs > 0 && (tabTotals?.[activeTab] || 0) === 0;

	// 3b: while a query is present the list area is replaced by the grouped
	// search surface (CONTACTS + MESSAGES). Requests tab keeps its own panel.
	const isSearching = Boolean(sidebarSearchQuery.trim());

	// Opening a result closes the surface (clear query + scope) and deep-links the
	// thread via the hook's watch+classify path so it lands in the right tab.
	const handleOpenFromSearch = React.useCallback(
		async (cid) => {
			await openThreadByCid(cid);
			setSidebarSearchQuery("");
			setSearchScope("all");
			setSearchFocused(false);
		},
		[openThreadByCid, setSidebarSearchQuery, setSearchScope],
	);

	const authRoles = useSelector((state) => state.auth.roles);
	const isLawyerUser =
		Array.isArray(authRoles) && authRoles.includes(ROLES.Lawyer);

	// 5o / 5h: a DM-only role (no tab bar) shows its role tag next to "Messages"
	// — Lawyer (purple), Player (green), Organiser (neutral). Dual roles that see
	// a tab bar never show it (the tabs already carry the identity).
	const dmOnlyRoleTag = (() => {
		if (showTabBar || !Array.isArray(authRoles)) return null;
		if (authRoles.includes(ROLES.Lawyer))
			return { label: "Lawyer", bg: "#F4F0FF", border: "#E4D9FB", color: "#6941C6" };
		if (authRoles.includes(ROLES.Player))
			return { label: "Player", bg: "#EAF6EE", border: "#CFEAD8", color: "#17803D" };
		if (authRoles.includes(ROLES.Organizer))
			return { label: "Organiser", bg: "#F2F4F7", border: "#E4E9F0", color: "#475467" };
		return null;
	})();

	if (isLoading) {
		// D-010: a lawyer must see their Law Requests even while the chat
		// connection is still coming up (slow/flaky Stream shouldn't hide
		// pending requests). Render the left-panel shell with the requests
		// section now; only the chat list below waits for the connection.
		// Non-lawyers keep the original centered loader (zero change).
		if (isLawyerUser) {
			return (
				<div className="h-[calc(100vh-88px)] flex">
					<div className="w-full md:w-80 lg:w-[392px] flex flex-col h-full overflow-hidden bg-white border-r border-[#DBDBDB]">
						<div className="flex-none p-4 pb-2.5">
							<h1 className="text-[18px] font-bold text-[#101828]">Messages</h1>
						</div>
						<LawRequestsSection />
						<div className="flex-1 flex flex-col items-center justify-center text-gray-500 gap-2">
							<LoadingIndicator size={28} />
							<span className="text-sm">Connecting to chat...</span>
						</div>
					</div>
					<div className="flex-1 hidden md:flex items-center justify-center text-gray-400">
						<span className="text-sm">Connecting to chat...</span>
					</div>
				</div>
			);
		}
		return (
			<div className="flex items-center justify-center h-[calc(100vh-144px)]">
				<LoadingIndicator size={40} />
				<span className="ml-4 text-gray-500">Connecting to chat...</span>
			</div>
		);
	}

	return (
		<>
			<style>{streamOverrides}</style>

			{videoClient && (
				<StreamVideo client={videoClient}>
					<StreamTheme>
						<Chat
							client={chatClient}
							theme="str-chat__theme-light"
							i18nInstance={streami18n}
							defaultLanguage={STREAM_CHAT_FALLBACK_LANGUAGE}
						>
							<ChatActiveChannelBridge
								onChannel={setCtxChannel}
								setterRef={ctxSetterRef}
							/>
							<div className="tp-inbox-viewport flex">
								<div className={`${hasThreadOpen ? "hidden md:flex" : "flex"} w-full md:w-80 lg:w-[392px] flex-col h-full overflow-hidden bg-white border-r border-[#DBDBDB]`}>
									<div className="flex-none p-4 pb-2.5">
										<div className="flex items-center justify-between mb-2.5">
											<div className="flex items-center gap-2.5">
												<h1 className="text-[18px] font-bold text-[#101828]">Messages</h1>
												{/* 5o/5h DM-only role tag. */}
												{dmOnlyRoleTag && (
													<span
														className="rounded-[6px] font-semibold"
														style={{
															padding: "2px 8px",
															fontSize: "10.5px",
															background: dmOnlyRoleTag.bg,
															border: `1px solid ${dmOnlyRoleTag.border}`,
															color: dmOnlyRoleTag.color,
														}}
													>
														{dmOnlyRoleTag.label}
													</span>
												)}
											</div>
											<div className="flex items-center gap-2.5">
												<button
													type="button"
													aria-label="Open Meetings"
													onClick={goToMeetings}
													className="inline-flex items-center gap-[7px] px-[14px] py-2 text-[12.5px] font-semibold text-[#344054] border border-[#E4E9F0] rounded-[9px] hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
												>
													{/* Calendar glyph precedes the label (design frame 3a). */}
													<svg
														className="w-[13px] h-[13px] flex-shrink-0 text-[#344054]"
														viewBox="0 0 24 24"
														fill="none"
														stroke="currentColor"
														aria-hidden="true"
													>
														<rect
															x="3"
															y="4.5"
															width="18"
															height="16"
															rx="2"
															strokeWidth={1.6}
														/>
														<path
															strokeLinecap="round"
															strokeWidth={1.6}
															d="M3 9h18M8 3v3M16 3v3"
														/>
													</svg>
													My Meetings
												</button>
												{/* New-chat "+" (design frame 1a): sits right-outermost
												    in the header action group [My Meetings][+]. Opens a
												    compact recipient picker over the existing endpoint. */}
												<button
													type="button"
													onClick={() => setShowNewChat(true)}
													aria-label="New chat"
													title="New chat"
													className="flex h-[32px] w-[32px] flex-shrink-0 items-center justify-center rounded-lg border border-[#E4E9F0] text-[#101828] hover:bg-gray-50 focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 transition-colors"
												>
													<svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
														<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 5v14M5 12h14" />
													</svg>
												</button>
											</div>
										</div>
									</div>

									{/* Tabbed inbox (D1/D16/D17). Perspective-specific underline
									    tab set with in-bar Priority+ "More ▾" overflow on desktop.
									    DM-only roles (Lawyer/Player/Organiser) hide the bar
									    entirely (D20). Side padding 10px per design; pb-2.5 keeps
									    the 10px gap to the search field. */}
									{showTabBar && (
										<div className="flex-none px-[10px] pb-2.5">
											<InboxTabs
												tabs={inboxTabs}
												active={activeTab}
												onChange={setActiveTab}
											/>
										</div>
									)}

									<div className="flex-none px-[12px] pb-2.5">
										<div className="relative">
											<input
												type="text"
												value={sidebarSearchQuery}
												onChange={handleSidebarSearch}
												onKeyDown={handleSidebarSearchKeyDown}
												onFocus={() => setSearchFocused(true)}
												onBlur={() => {
													setSearchFocused(false);
													// Reset the scope once search is abandoned so the
													// resting list is never silently scoped.
													if (!sidebarSearchQuery) setSearchScope("all");
												}}
												placeholder="Search conversations"
												className="w-full h-[31px] pl-[31px] pr-[10px] text-[12.5px] text-[#101828] bg-[#F5F7FA] border border-[#E9EDF2] rounded-[9px] placeholder:text-[12.5px] placeholder:font-normal placeholder:text-[#98A2B3] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
											/>
											<svg
												className="absolute left-[10px] top-1/2 transform -translate-y-1/2 w-[13px] h-[13px] text-[#98A2B3] pointer-events-none"
												fill="none"
												stroke="currentColor"
												viewBox="0 0 24 24"
											>
												<path
													strokeLinecap="round"
													strokeLinejoin="round"
													strokeWidth={1.6}
													d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
												/>
											</svg>
											{sidebarSearchQuery && (
												<button
													onClick={() => setSidebarSearchQuery("")}
													className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
												>
													<svg
														className="w-4 h-4"
														fill="none"
														stroke="currentColor"
														viewBox="0 0 24 24"
													>
														<path
															strokeLinecap="round"
															strokeLinejoin="round"
															strokeWidth={2}
															d="M6 18L18 6M6 6l12 12"
														/>
													</svg>
												</button>
											)}
										</div>

										{/* 4b: scope chips appear on search focus; mousedown-preventDefault
										    keeps the input focused so a chip click doesn't dismiss the row. */}
										{showScopeChips && (
											<div className="flex items-center gap-1.5 mt-2 text-[11.5px] font-semibold overflow-x-auto">
												<span className="text-[#98A2B3] font-medium flex-shrink-0">
													Search in:
												</span>
												{searchScopes.map((scope) => {
													const active = searchScope === scope.key;
													return (
														<button
															key={scope.key}
															type="button"
															onMouseDown={(e) => e.preventDefault()}
															onClick={() => setSearchScope(scope.key)}
															className="flex-shrink-0 rounded-full border transition-colors"
															style={{
																padding: "3px 10px",
																background: active ? "#EAF2FF" : "#FFFFFF",
																borderColor: active ? "#BBD7FF" : "#E4E9F0",
																color: active ? "#1466D8" : "#475467",
															}}
														>
															{scope.label}
														</button>
													);
												})}
											</div>
										)}
									</div>

									{/* D11: DM-tab intent chips — permanently visible under the search,
									    DM lane only (the tab is the filter elsewhere). Hidden while the
									    search surface is up. Distinct from the 4b "Search in:" chips. */}
									{activeTab === "dm" && !isSearching && (
										<div className="flex-none px-[12px] pb-2.5">
											<DmIntentChips
												counts={dmIntentCounts}
												active={intentFilter}
												onChange={setIntentFilter}
												t={t}
											/>
										</div>
									)}

									{/* D-010: lawyer-only "Law Requests" grouping above the chat
									    list. Renders null for non-lawyers (inbox unchanged). */}
									<LawRequestsSection />

									{isSearching ? (
										/* 3b/4b: grouped search surface replaces the list while
										   a query is active. Cross-tab CONTACTS + MESSAGES. */
										<div className="flex-1 overflow-y-auto overscroll-y-contain min-h-0">
											<InboxSearchResults
												chatClient={chatClient}
												userId={userId}
												query={sidebarSearchQuery.trim()}
												searchScope={searchScope}
												onWidenScope={() => setSearchScope("all")}
												onOpenChannel={handleOpenFromSearch}
											/>
										</div>
									) : (
										<div className="flex-1 flex flex-col min-h-0">
											{/* D29: per-lane cold-contact gate. Only DM / Last Minute
											    / Agent Req. host message requests, scoped to THIS
											    lane's origin (no origin control — the lane IS the
											    filter). Collapses to nothing when the lane has none.
											    key={activeTab} remounts it per lane so its fetch
											    re-scopes cleanly. */}
											{activeLaneRequestOrigin && (
												<div className="flex max-h-[52%] flex-none flex-col overflow-hidden border-b border-[#F0F2F6]">
													<MessageRequestsPanel
														key={activeTab}
														embedded
														defaultView={requestsView}
														laneOrigin={activeLaneRequestOrigin}
														selectedId={selectedRequest?.id}
														onSelect={setSelectedRequest}
														onOpenThread={openThreadByCid}
														onCountsChange={(n) => {
															setPendingRequestsCount(n);
															// Only spread a new object when the lane's count
															// actually changed — an unconditional {...prev}
															// re-rendered Inbox after EVERY panel fetch, which
															// (before the panel decoupled its fetch from this
															// callback's identity) fed the endless-refetch loop
															// behind the never-ending list spinner.
															setPendingRequestsByLane((prev) =>
																prev[activeTab] === n
																	? prev
																	: { ...prev, [activeTab]: n },
															);
														}}
														onProvideActions={(actions) => {
															requestActionsRef.current = actions;
														}}
														t={t}
													/>
												</div>
											)}

											{/* Rows sit directly under the search — no section label
											   (design 1a/7a drop the old "Recently contacted" header).
												   `overscroll-y-contain`: the list's bounce stays local —
												   no scroll chaining into the requests section above it
												   (iOS felt rubbery — Marin, mobile-sweep follow-up). */}
											<div className="flex-1 overflow-y-auto overscroll-y-contain min-h-0 ml-4">
												<ChannelList
													key={`main-${listRefreshKey}`}
													filters={filters}
													sort={sort}
													options={options}
													Preview={MainPreview}
													/* T031 (symptom 1): never auto-open the
													   most-recently-active chat on mount. Stream's
													   default (setActiveChannelOnMount = true)
													   selects channels[0] once the first query
													   resolves, which both raced the profile
													   "Message" deep link and left QA typing into
													   the WRONG thread. With no selection the pane
													   shows the "Select a chat" placeholder and a
													   deep link selects its own channel. */
													setActiveChannelOnMount={false}
													setActiveChannel={setActiveChannel}
													channelRenderFilterFn={listChannelFilterFn}
													EmptyStateIndicator={ListEmptyState}
													LoadingIndicator={() => (
														<div className="p-4 text-center text-gray-500">
															Loading chats...
														</div>
													)}
												/>

												{/* #23: active tab filtered to zero while other tabs
												    still hold channels — the ChannelList renders nothing
												    and its own indicator can't fire, so show ours. */}
												{showTabEmptyState && <ListEmptyState />}


											</div>

											{/* Archive = PINNED footer of the left column (design 1c/5a:
											    "bottom strip"), visible on every tab WITHOUT scrolling —
											    Marin feedback 2026-07-16. The expanded panel scrolls
											    inside itself so the footer never swallows the column. */}
											<div className="flex-none">
												{/* Archive section per tab. Product decision (Marin,
												    overrides the design which omits the row at zero): the
												    row is ALWAYS rendered (see InboxArchiveSection). The
												    archived list below mounts only when expanded AND there
												    are expired chats, so its query never fires at zero. */}
												<InboxArchiveSection
													manualCount={archiveManualCount}
													expiredCount={archiveExpiredCount}
													expanded={showArchive}
													onToggle={() => setShowArchive((v) => !v)}
												>
													{/* D24 species groups (frame 5i): manual archives
													    (writable, Unarchive action) are visually
													    separated from expired (read-only, permanent).
													    Each group's ChannelList mounts only when that
													    species has rows, so its query never fires empty. */}
													{archiveManualCount > 0 && (
														<>
															<ArchiveGroupHeader>
																ARCHIVED BY YOU — STILL WRITABLE
															</ArchiveGroupHeader>
															<ChannelList
																key={`arch-manual-${listRefreshKey}`}
																filters={archivedFilters}
																sort={sort}
																options={options}
																Preview={ArchiveManualPreview}
																setActiveChannelOnMount={false}
																setActiveChannel={setActiveChannel}
																channelRenderFilterFn={archiveManualFilterFn}
																EmptyStateIndicator={() => null}
															/>
														</>
													)}
													{archiveExpiredCount > 0 && (
														<>
															<ArchiveGroupHeader>
																EXPIRED — READ-ONLY, PERMANENT
															</ArchiveGroupHeader>
															<ChannelList
																key={`arch-expired-${listRefreshKey}`}
																filters={archivedFilters}
																sort={sort}
																options={options}
																Preview={ArchiveExpiredPreview}
																setActiveChannelOnMount={false}
																setActiveChannel={setActiveChannel}
																channelRenderFilterFn={archiveExpiredFilterFn}
																EmptyStateIndicator={() => null}
															/>
														</>
													)}
												</InboxArchiveSection>
											</div>
										</div>
									)}
								</div>

								{/* Thread pane: message window + persistent participants rail
								    (design Turn 5, 5a/5e). The rail is a sibling column of the
								    chat window and self-gates (club<->agent channels only,
								    desktop only). When a per-lane cold-contact request is
								    selected (D29), this pane hosts its detail view instead
								    (design 1f, #11). */}
								<div className={`${hasThreadOpen ? "flex" : "hidden md:flex"} flex-1 h-full overflow-hidden bg-white`}>
									{selectedRequest ? (
										<div className="flex flex-1 flex-col h-full min-w-0 overflow-hidden">
											{/* QA-3 / 5n: mobile-only back to the list (single column). */}
											<button
												type="button"
												onClick={() => setSelectedRequest(null)}
												className="md:hidden flex-none flex items-center gap-1 h-11 px-3 border-b border-[#E4E9F0] bg-white text-[13px] font-semibold text-[#1466D8]"
												aria-label="Back to messages"
											>
												<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
													<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
												</svg>
												Messages
											</button>
											<div className="flex-1 min-h-0">
												<RequestDetailPane
													request={selectedRequest}
													onAccept={(id) =>
														requestActionsRef.current.accept?.(id)
													}
													onIgnore={(id) =>
														requestActionsRef.current.ignore?.(id)
													}
													onOpenThread={openThreadByCid}
													onClose={() => setSelectedRequest(null)}
													t={t}
												/>
											</div>
										</div>
									) : (
									<>
									<div className="flex-1 flex flex-col h-full overflow-hidden min-w-0 bg-white">
									{/* QA-3 / 5n: mobile-only back control — single-column inbox has
									    no visible list while a thread is open, so this returns to it
									    (desktop keeps both panes, so it's hidden there). */}
									<button
										type="button"
										onClick={clearOpenThread}
										className="md:hidden flex-none flex items-center gap-1 h-11 px-3 border-b border-[#E4E9F0] bg-white text-[13px] font-semibold text-[#1466D8]"
										aria-label="Back to messages"
									>
										<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
											<path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
										</svg>
										Messages
									</button>
									<Channel
										channel={activeChannel}
										Message={CustomMessage}
										/* Message={CustomMessage} fully replaces Stream's default
										   renderer, so this Attachment prop is never consulted by
										   Stream. Custom attachment cards are actually routed by
										   CustomMessage, which delegates unrecognised types to
										   CustomAttachment. Kept here as a harmless, future-proof
										   default in case Stream's own renderer is ever used. */
										Attachment={CustomAttachment}
										className="flex-1 min-h-0 !bg-white"
										LoadingIndicator={() => (
											<div className="flex items-center justify-center h-full">
												<LoadingIndicator size={30} />
												<span className="ml-3 text-gray-400 text-sm">
													Loading channel...
												</span>
											</div>
										)}
										/* T031 (symptom 2): TWO distinct empty states.
										   EmptyPlaceholder renders when NO chat is selected at
										   all; EmptyStateIndicator is what stream-chat-react
										   mounts inside the MessageList of an OPEN chat that has
										   zero messages. The old inline "Select a chat" copy sat
										   on the second slot, so a freshly created, message-less
										   chat looked unselected although its header, composer
										   and rail were already showing it. */
										EmptyPlaceholder={<SelectAChatPlaceholder />}
										EmptyStateIndicator={NoMessagesYetIndicator}
									>
										<Window>
											{/* Header rendered as a fixed flex-none bar at the
											    top of the message pane — NOT as Stream's
											    HeaderComponent, which mounts inside the scrolling
											    .str-chat__list as an intro element and scrolls
											    away with the conversation (parity #1). It reads
											    the active channel from useChannelStateContext, so
											    mounting it inside <Window> keeps it in sync. */}
											<CustomChatHeader
												videoClient={videoClient}
												onStartCall={handleStartCall}
												onChannelLeave={clearOpenThread}
											/>
											<CustomWindow>
												<MessageList DateSeparator={ChatDateSeparator} />
												<CustomMessageInput />
											</CustomWindow>
										</Window>
									</Channel>
									</div>
									<ParticipantsRail channel={activeChannel} />
					</>
					)}
								</div>
							</div>
						</Chat>
					</StreamTheme>
				</StreamVideo>
			)}

			{showNewChat && (
				<NewChatModal
					onOpenThread={(cid) => {
						openThreadByCid(cid);
						setShowNewChat(false);
					}}
					onClose={() => setShowNewChat(false)}
					/* Frame 1r escape hatch from the picker's compose modal: the
					   gate is already on screen behind it, so just flip it to Sent
					   (the DM lane always hosts the gate for an agent). */
					onViewPending={() => {
						setActiveTab("dm");
						setRequestsView("sent");
					}}
				/>
			)}

			{showIncomingCallModal && incomingCall && (
				<IncomingCallScreen
					incomingCall={incomingCall}
					onAccept={acceptIncomingCall}
					onDecline={declineIncomingCall}
				/>
			)}

			{isInCall && call && videoClient && (
				<StreamCall call={call}>
					{/* BackgroundFiltersProvider lazy-loads TFLite (~3MB WASM + 400KB model)
					    from /tf/. Provider only renders during an active call so the
					    download doesn't happen on inbox open.

					    onError surfaces any TFLite / WASM load failure to the user as
					    a toast instead of letting the blur button silently no-op. */}
					<BackgroundFiltersProvider
						basePath="/tf"
						tfFilePath="/tf/tflite/tflite-simd.wasm"
						modelFilePath="/tf/models/segm_full_v679.tflite"
						onError={(err) => {
							console.error("Background filter load failed:", err);
							toast.error(
								"Background blur isn't available right now. " +
								"Please refresh and try again.",
								{ duration: 5000 },
							);
						}}
					>
						<CallModal
							call={call}
							callType={callType}
							onEndCall={handleEndCall}
							chatClient={chatClient}
						/>
					</BackgroundFiltersProvider>
				</StreamCall>
			)}
		</>
	);
};

export default Inbox;

